import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Button,
  createTheme,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Tab,
  Tabs,
  ThemeProvider,
  Toolbar,
  Typography,
} from '@mui/material';
import { Settings } from '@mui/icons-material';
import { showTokenModal } from '../components/admin/TokenModal';
import { useConfig } from '../ConfigContext';
import FileSelectorModal from '../components/admin/FileSelectorModal';
import { Config } from '../App';
import ThemeEditor from '../components/Editor/Components/ThemeEditor';
import FontsEditor from '../components/Editor/Components/FontsEditor';
import PageEditor from '../components/Editor/Components/PageEditor';
import FooterEditor from '../components/Editor/Components/FooterEditor';
import {
  saveConfigVersion,
  getConfigVersions,
  deleteConfigVersion,
} from '../utils/version-control';

// Create a dedicated theme for the editor to isolate it from the site's theme
const editorTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#000000', // Black for primary actions like Publish
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    subtitle2: {
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.05em',
    },
    caption: {
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.7rem',
      letterSpacing: '0.05em',
      color: '#999',
    },
    body2: {
      fontSize: '0.875rem',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          textTransform: 'uppercase',
          fontWeight: 700,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0px 2px 4px rgba(0,0,0,0.05)',
          border: '1px solid #e0e0e0',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: '0.75rem',
        },
      },
    },
  },
});

const EditorPage: React.FC = () => {
  const { config, handleUpload } = useConfig();
  const [currentConfig, setCurrentConfig] = useState<Config | null>(null);
  const [token, setToken] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [activeThemeTab, setActiveThemeTab] = useState(0);

  // File selector state
  const [isFileSelectorOpen, setIsFileSelectorOpen] = useState(false);
  const [activeFileField, setActiveFileField] = useState<string | null>(null);
  const [fileSelectCallback, setFileSelectCallback] = useState<
    ((url: string) => void) | null
  >(null);

  // Settings modal state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [adminMode, setAdminMode] = useState(() => {
    return localStorage.getItem('editorAdminMode') === 'true';
  });

  useEffect(() => {
    if (config) {
      setCurrentConfig(config);
    }
  }, [config]);

  useEffect(() => {
    localStorage.setItem('editorAdminMode', String(adminMode));
  }, [adminMode]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    setCheckingAuth(true);
    // Bypass auth for now
    setIsAuthenticated(true);
    setCheckingAuth(false);
    /*
    const authToken = await showTokenModal();
    if (authToken) {
      setToken(authToken);
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
    setCheckingAuth(false);
    */
  };

  const handleThemeChange = (key: string, value: any) => {
    if (!currentConfig) return;

    setCurrentConfig((prevConfig) => {
      if (!prevConfig) return null;

      const keys = key.split('.');
      const lastKey = keys.pop();
      const newTheme = { ...prevConfig.theme };

      let nestedConfig = newTheme;
      keys.forEach((k) => {
        nestedConfig[k] = { ...nestedConfig[k] };
        nestedConfig = nestedConfig[k];
      });

      if (lastKey) {
        nestedConfig[lastKey] = value;
      }

      return {
        ...prevConfig,
        theme: newTheme,
      };
    });
  };

  const handleFileSelect = (fileUrl: string) => {
    if (activeFileField) {
      handleThemeChange(activeFileField, fileUrl);
    } else if (fileSelectCallback) {
      fileSelectCallback(fileUrl);
    }
    setIsFileSelectorOpen(false);
    setActiveFileField(null);
    setFileSelectCallback(null);
  };

  const openFileSelector = (field: string) => {
    setActiveFileField(field);
    setIsFileSelectorOpen(true);
  };

  const openFileSelectorForCallback = (callback: (url: string) => void) => {
    setFileSelectCallback(() => callback);
    setIsFileSelectorOpen(true);
  };

  // Autosave logic
  useEffect(() => {
    if (!currentConfig) return;

    const handler = setTimeout(() => {
      saveConfigVersion(currentConfig, 'Auto-save');
    }, 1000); // 1s debounce

    return () => {
      clearTimeout(handler);
    };
  }, [currentConfig]);

  const handleUndo = () => {
    if (!currentConfig) return;

    const versions = getConfigVersions();
    // Use first version if available, otherwise nothing to undo
    if (versions.length === 0) return;

    // Check if current matches versions[0] (Deep comparison simplified)
    const currentJson = JSON.stringify(currentConfig);
    const latestSavedJson = JSON.stringify(versions[0].config);

    if (currentJson !== latestSavedJson) {
      // Unsaved changes: Revert to the last saved version
      setCurrentConfig(versions[0].config);
    } else if (versions.length > 1) {
      // Already at latest version: Go back one step
      setCurrentConfig(versions[1].config);
      // Remove the "future" version we are stepping back from
      deleteConfigVersion(versions[0].id);
    }
  };

  const handlePublish = async () => {
    if (!currentConfig) return;

    // Save to server
    const blob = new Blob([JSON.stringify(currentConfig, null, 2)], {
      type: 'application/json',
    });
    const hostname = window.location.hostname;
    const filename =
      hostname === 'localhost' ? 'localhost.json' : `${hostname}.json`;

    const success = await handleUpload(blob, filename);

    if (success) {
      // Save version to localStorage
      saveConfigVersion(currentConfig);
      alert('Configuration saved successfully!');
    } else {
      alert('Failed to save configuration.');
    }
  };

  if (checkingAuth) {
    return <Box sx={{ p: 3 }}>Checking authentication...</Box>;
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          Access Denied
        </Typography>
        <Button variant="contained" onClick={checkAuth}>
          Login
        </Button>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={editorTheme}>
      <CssBaseline />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          bgcolor: '#f5f5f5',
        }}
      >
        <AppBar
          position="static"
          sx={{
            backgroundColor: '#c0f0daff', // Light green similar to image
            color: 'black',
            boxShadow: 'none',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Toolbar
            variant="dense"
            sx={{ justifyContent: 'space-between', minHeight: '64px' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Button
                variant="outlined"
                color="inherit"
                href="/#/"
                target="_blank"
                sx={{
                  borderColor: 'black',
                  color: 'black',
                  textTransform: 'uppercase',
                  borderRadius: 0,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  padding: '6px 16px',
                }}
              >
                Open the site
              </Button>

              <Tabs
                value={activeTab}
                onChange={(e, val) => setActiveTab(val)}
                textColor="inherit"
                indicatorColor="primary"
                sx={{
                  minHeight: '64px',
                  '& .MuiTab-root': {
                    minHeight: '64px',
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'black',
                    height: '3px',
                  },
                }}
              >
                <Tab label="PAGES" />
                <Tab label="THEME" />
                <Tab label="FOOTER" />
              </Tabs>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleUndo}
                sx={{
                  borderColor: 'black',
                  color: 'black',
                  borderRadius: 0,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                }}
              >
                UNDO
              </Button>
              <Button
                variant="contained"
                onClick={handlePublish}
                sx={{
                  backgroundColor: 'black',
                  color: 'white',
                  '&:hover': { backgroundColor: '#333' },
                  borderRadius: 0,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                }}
              >
                PUBLISH
              </Button>
              <IconButton
                color="inherit"
                onClick={() => setIsSettingsOpen(true)}
              >
                <Settings />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Settings Modal */}
        <Dialog
          open={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          aria-labelledby="settings-dialog-title"
        >
          <DialogTitle id="settings-dialog-title">Editor Settings</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Configure local editor behavior.
            </DialogContentText>
            <FormControlLabel
              control={
                <Switch
                  checked={adminMode}
                  onChange={(e) => setAdminMode(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Admin Mode</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Enables advanced options in this browser.
                  </Typography>
                </Box>
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsSettingsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          {activeTab === 0 && (
            <PageEditor
              config={currentConfig}
              onConfigChange={(newConfig) => setCurrentConfig(newConfig)}
              onOpenFileSelector={openFileSelectorForCallback}
              isAdmin={adminMode}
            />
          )}
          {activeTab === 1 && (
            <Box sx={{ p: 3, overflow: 'auto', height: '100%' }}>
              <Box>
                <Tabs
                  value={activeThemeTab}
                  onChange={(e, val) => setActiveThemeTab(val)}
                  textColor="inherit"
                  indicatorColor="primary"
                  sx={{
                    borderBottom: '1px solid #e0e0e0',
                    mb: 3,
                    '& .MuiTab-root': {
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      minHeight: '48px',
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: 'black',
                      height: '2px',
                    },
                  }}
                >
                  <Tab label="COLORS & STYLES" />
                  <Tab label="FONTS" />
                  <Tab label="BUTTONS" />
                </Tabs>
                <Box sx={{ mt: 2 }}>
                  {activeThemeTab === 0 && currentConfig && (
                    <ThemeEditor
                      config={currentConfig}
                      onThemeChange={handleThemeChange}
                      onOpenFileSelector={openFileSelector}
                    />
                  )}
                  {activeThemeTab === 1 && (
                    <FontsEditor
                      config={currentConfig}
                      onThemeChange={handleThemeChange}
                    />
                  )}
                  {activeThemeTab === 2 && (
                    <Typography>Buttons content goes here</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          {activeTab === 2 && currentConfig && (
            <FooterEditor
              config={currentConfig.footerConfig}
              onChange={(newFooterConfig) =>
                setCurrentConfig({
                  ...currentConfig,
                  footerConfig: newFooterConfig,
                })
              }
            />
          )}
        </Box>

        <FileSelectorModal
          open={isFileSelectorOpen}
          onClose={() => setIsFileSelectorOpen(false)}
          onSelect={handleFileSelect}
          configParam={window.location.hostname}
          handleUpload={handleUpload}
        />
      </Box>
    </ThemeProvider>
  );
};

export default EditorPage;
